import { useState, useEffect, useCallback, useMemo } from 'react'
import { isAllBlanksFilled } from '../../../utils/quiz-parser'
import type { SelectTextQuizData, QuizSolveResult } from '../../../types/legacyQuiz'

export interface UseSelectTextQuizOptions {
  quizId: string | number
  quiz: SelectTextQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

export interface SelectTextOptionModel {
  rawIndex: number
  label: string
  isMarker: boolean
  isCorrectMarked: boolean
  isSelectable: boolean
}

export function parseSelectTextContents(problem: string): string[] {
  const reg = /\{\{(.*?)}}/gim
  const contents: string[] = []
  let match: RegExpExecArray | null
  while ((match = reg.exec(problem)) !== null) {
    contents.push(match[1])
  }
  return contents
}

export function parseSelectTextOptions(content: string): string[] {
  return content.split('|').map(s => s.trim())
}

export function parseSelectTextOptionModels(content: string): SelectTextOptionModel[] {
  return parseSelectTextOptions(content).map((token, rawIndex) => {
    const isMarker = rawIndex === 0 && token === 'fix:'
    const label = token.replace(/`/g, '')

    return {
      rawIndex,
      label,
      isMarker,
      isCorrectMarked: token.includes('`'),
      isSelectable: !isMarker && label.length > 0,
    }
  })
}

export function useSelectTextQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseSelectTextQuizOptions) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [openDropdown, setOpenDropdown] = useState<number | null>(null)
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const selectData = useMemo(() => {
    return parseSelectTextContents(quiz.problem)
  }, [quiz.problem])

  const selectCount = selectData.length

  const [answers, setAnswers] = useState<string[]>(() => {
    if (initialAnswersProp !== undefined) {
      return new Array(selectCount).fill('').map((_, index) => initialAnswersProp[index] ?? '')
    }
    if (isViewSolved && solveResult?.correctAnswers) {
      return solveResult.correctAnswers.map(a => String(a))
    }
    return new Array(selectCount).fill('')
  })

  const isSolved = solveResult !== null && solveResult !== undefined
  const canSubmit = isAllBlanksFilled(answers)
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers

  useEffect(() => {
    onAnswerChange?.(answers)
  }, [answers, onAnswerChange])

  const problemKey = useMemo(
    () => `${quizId}-${selectCount}-${quiz.problem.length}`,
    [quizId, selectCount, quiz.problem]
  )

  const handleToggleDropdown = useCallback((index: number) => {
    if (isSolved || isViewSolved) return
    setOpenDropdown(prev => prev === index ? null : index)
    setCurrentIndex(index)
  }, [isSolved, isViewSolved])

  const handleSelect = useCallback((selectIndex: number, optionIndex: number) => {
    if (isSolved || isViewSolved) return
    setAnswers(prev => {
      const newAnswers = [...prev]
      newAnswers[selectIndex] = optionIndex.toString()
      return newAnswers
    })
    setOpenDropdown(null)
    if (selectIndex < selectCount - 1) {
      setCurrentIndex(selectIndex + 1)
    }
  }, [isSolved, isViewSolved, selectCount])

  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, answers, quiz.vars, elapsedTime)
  }, [answers, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime])

  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown !== null) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [openDropdown])

  return {
    answers,
    currentIndex,
    openDropdown,
    selectData,
    selectCount,
    problemKey,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggleDropdown,
    handleSelect,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
